import { politicaPrivacidadeSchema, type PoliticaPrivacidadeForm } from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { gestaoApi } from '../lib/gestao-api';

/**
 * O aviso de privacidade que o cliente lê e aceita ao abrir o chat.
 *
 * Fica aqui, e não no código, porque texto jurídico muda e cada correção não deveria
 * custar uma subida de sistema. O que o sistema guarda de cada aceite é a **versão** — é
 * por ela que se prova, meses depois, qual redação a pessoa aceitou.
 */
export function Privacidade() {
  const qc = useQueryClient();
  const atual = useQuery({ queryKey: ['politica'], queryFn: gestaoApi.lerPolitica });
  const [form, setForm] = useState<PoliticaPrivacidadeForm>({ versao: '1', texto: '' });
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (atual.data) setForm({ versao: atual.data.versao, texto: atual.data.texto });
  }, [atual.data]);

  const salvar = useMutation({
    mutationFn: (dados: PoliticaPrivacidadeForm) => gestaoApi.salvarPolitica(dados),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['politica'] });
      setSalvo(true);
      setErro(null);
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui publicar'),
  });

  function publicar() {
    const parse = politicaPrivacidadeSchema.safeParse(form);
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    setSalvo(false);
    salvar.mutate(parse.data);
  }

  const textoMudou = !!atual.data && atual.data.texto !== form.texto;
  const versaoIgual = !!atual.data && atual.data.versao === form.versao;

  return (
    <div className="max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Aviso de privacidade</h1>
        <p className="text-sm text-neutral-500">
          É o que o cliente lê antes de iniciar um atendimento ou criar uma conta. Publicado aqui,
          aparece na hora no chat.
        </p>
      </header>

      {atual.data?.atualizadoEm ? (
        <p className="text-xs text-neutral-500">
          Versão <strong>{atual.data.versao}</strong>, publicada em{' '}
          {new Date(atual.data.atualizadoEm).toLocaleString('pt-BR')}.
        </p>
      ) : (
        <Aviso tipo="atencao">
          Nada publicado ainda. Enquanto este texto não existir, o cliente aceita uma versão que ele
          não consegue ler — e um aceite que não dá para ler não vale como consentimento.
        </Aviso>
      )}

      <CampoTexto
        rotulo="Versão"
        value={form.versao}
        onChange={(e) => setForm({ ...form, versao: e.target.value })}
        className="max-w-40"
        ajuda="Use o que fizer sentido para você: 1, 2, ou a data (2026-09-27)."
      />

      {textoMudou && versaoIgual && (
        <Aviso tipo="atencao">
          Você mudou o texto sem mudar a versão. Quem já aceitou continuará marcado como tendo
          aceitado esta versão — que agora diz outra coisa. Se a mudança for de conteúdo, e não de
          vírgula, troque a versão.
        </Aviso>
      )}

      <label className="block text-sm font-medium">
        Texto
        <textarea
          rows={22}
          value={form.texto}
          onChange={(e) => setForm({ ...form, texto: e.target.value })}
          placeholder="Quem somos, que dados coletamos, para quê, com quem compartilhamos, por quanto tempo guardamos, quais são os direitos do titular e como falar com o encarregado."
          className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 font-mono text-sm dark:border-neutral-700 dark:bg-neutral-900"
        />
      </label>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {salvo && <Aviso tipo="sucesso">Publicado. Já está no ar para o cliente.</Aviso>}

      <div className="flex justify-end">
        <Botao carregando={salvar.isPending} onClick={publicar}>
          Publicar
        </Botao>
      </div>
    </div>
  );
}
